import { useEffect } from 'react';
import { isNative } from '../platform/native';

/** After this long without a tap or key, the screen may sleep again (a task left open). */
export const WAKE_IDLE_MS = 15 * 60 * 1000;

type Sentinel = { released: boolean; release: () => Promise<void> };

/**
 * Keeps the screen on while `active` (2026-10-13, Jens): children working through a task's steps
 * shouldn't watch the tablet go dark. Uses the Screen Wake Lock API where the browser has it
 * (Chrome/Edge, Android, Safari 16.4+); elsewhere it does nothing. The browser drops the lock
 * when the app is hidden, so it is taken again on return; after WAKE_IDLE_MS without input it is
 * let go (saves the battery if a task is left open) and taken again on the next tap.
 */
export function useWakeLock(active: boolean): void {
  useEffect(() => {
    // In the iOS/Android apps the native keep-awake plugin does it (the web API isn't there).
    const api = isNative ? nativeWakeLock : (navigator as any).wakeLock as { request: (type: 'screen') => Promise<Sentinel> } | undefined;
    if (!active || !api) return;

    let sentinel: Sentinel | null = null;
    let stopped = false;
    let idleTimer: number | undefined;

    const acquire = async () => {
      if (stopped || document.visibilityState !== 'visible' || (sentinel && !sentinel.released)) return;
      try {
        sentinel = await api.request('screen');
        if (stopped) void sentinel.release();
      } catch {
        /* not allowed right now (e.g. battery saver): fine, the screen just behaves as usual */
      }
    };
    const release = () => {
      const s = sentinel;
      sentinel = null;
      if (s && !s.released) void s.release().catch(() => undefined);
    };
    const armIdle = () => {
      window.clearTimeout(idleTimer);
      idleTimer = window.setTimeout(release, WAKE_IDLE_MS);
    };
    const onInput = () => { armIdle(); void acquire(); };
    const onVisible = () => { if (document.visibilityState === 'visible') onInput(); };

    void acquire();
    armIdle();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('pointerdown', onInput);
    window.addEventListener('keydown', onInput);
    return () => {
      stopped = true;
      window.clearTimeout(idleTimer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('pointerdown', onInput);
      window.removeEventListener('keydown', onInput);
      release();
    };
  }, [active]);
}

/** The native keep-awake plugin, shaped like the Screen Wake Lock API used above. */
const nativeWakeLock = {
  request: async (): Promise<Sentinel> => {
    const { KeepAwake } = await import('@capacitor-community/keep-awake');
    await KeepAwake.keepAwake();
    const sentinel: Sentinel = {
      released: false,
      release: async () => { sentinel.released = true; await KeepAwake.allowSleep(); },
    };
    return sentinel;
  },
};
